// components/organization/OrgQuickLinks.jsx
import { Link } from "react-router-dom";
import { Plus, Users, Layers, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

// ---- helpers ----
function matchWithWildcard(perms, key) {
  if (!Array.isArray(perms)) return false;
  if (perms.includes("*") || perms.includes("platform:*")) return true;
  if (perms.includes(key)) return true;
  const parts = key.split(":");
  for (let i = parts.length; i >= 1; i--) {
    const cand = parts.slice(0, i).join(":") + ":*";
    if (perms.includes(cand)) return true;
  }
  return false;
}
const canRead = (perms, resource) =>
  matchWithWildcard(perms, `org:${resource}:read`) || canWrite(perms, resource);
const canWrite = (perms, resource) =>
  matchWithWildcard(perms, `org:${resource}:write`);

const OrgQuickLinks = ({ perms = [] }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");

  const linkThemes = {
    [t("org_quick_links.create_exam")]: {
      bgClass: "bg-indigo-50",
      textClass: "text-indigo-700",
      iconBgClass: "bg-indigo-100",
      iconColorClass: "text-indigo-600",
      hoverClass: "hover:shadow-lg hover:border-indigo-300",
    },
    [t("org_quick_links.manage_users")]: {
      bgClass: "bg-green-50",
      textClass: "text-green-700",
      iconBgClass: "bg-green-100",
      iconColorClass: "text-green-600",
      hoverClass: "hover:shadow-lg hover:border-green-300",
    },
    [t("org_quick_links.manage_categories")]: {
      bgClass: "bg-orange-50",
      textClass: "text-orange-700",
      iconBgClass: "bg-orange-100",
      iconColorClass: "text-orange-600",
      hoverClass: "hover:shadow-lg hover:border-orange-300",
    },
    [t("org_quick_links.view_exams")]: {
      bgClass: "bg-blue-50",
      textClass: "text-blue-700",
      iconBgClass: "bg-blue-100",
      iconColorClass: "text-blue-600",
      hoverClass: "hover:shadow-lg hover:border-blue-300",
    },
    default: {
      bgClass: "bg-purple-50",
      textClass: "text-purple-700",
      iconBgClass: "bg-purple-100",
      iconColorClass: "text-purple-600",
      hoverClass: "hover:shadow-lg hover:border-purple-300",
    },
  };

  // هر لینک شرط نمایش خودش را دارد:
  const links = [
    {
      label: t("org_quick_links.create_exam"),
      href: "/organization/exams",
      icon: <Plus size={24} />,
      show: () => canWrite(perms, "exams"), // ساخت/مدیریت آزمون → write
    },
    {
      label: t("org_quick_links.manage_users"),
      href: "/organization/users",
      icon: <Users size={24} />,
      show: () => canRead(perms, "users"), // دیدن لیست کاربران → حداقل read
    },
    {
      label: t("org_quick_links.manage_categories"),
      href: "/organization/categories",
      icon: <Layers size={24} />,
      show: () => canRead(perms, "categories"), // مدیریت/مشاهده دسته‌بندی‌ها → حداقل read
    },
    {
      label: t("org_quick_links.view_exams"),
      href: "/organization/exams",
      icon: <FileText size={24} />,
      show: () => canRead(perms, "exams"), // مشاهده آزمون‌ها → read
    },
  ];

  const visible = links.filter((l) => l.show());

  if (visible.length === 0) return null;

  return (
    <div
      className={`grid sm:grid-cols-2 md:grid-cols-4 gap-4 ${
        isRtl ? "text-right" : "text-left"
      }`}
    >
      {visible.map((item, i) => {
        const theme = linkThemes[item.label] || linkThemes.default;
        return (
          <Link
            key={i}
            to={item.href}
            className={`
              ${theme.bgClass} ${theme.hoverClass}
              rounded-xl border-2 border-transparent
              flex flex-col items-center justify-center
              py-8 px-4 transition-all duration-300 ease-in-out
              transform hover:scale-[1.03] hover:z-10 shadow-md
            `}
          >
            <div
              className={`${theme.iconBgClass} ${theme.iconColorClass} rounded-full p-4 mb-4 flex items-center justify-center`}
            >
              {item.icon}
            </div>
            <span
              className={`text-lg font-bold ${theme.textClass} text-center`}
            >
              {item.label}
            </span>
          </Link>
        );
      })}
    </div>
  );
};

export default OrgQuickLinks;
